import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AftersalesOrder } from '@1po/1po-bff-fe-spec/generated/aftersales_order/model/AftersalesOrder';
import {
  AftersalesInvoice,
  AftersalesOrderItem,
} from '@1po/1po-bff-fe-spec/generated/aftersales_order/response/AftersalesOrderResponse';
import { Table } from 'antd-v5';
import { getDepositReturnListEnabled, getExadisReturnListEnabled } from 'domains/appContext/AppContext.store';
import { getOrderClaimForm, initialState, setOrderClaimForm } from 'domains/order/Order.store';
import { AddDepositReturnListItemSelection, AddExadisReturnListItemSelection } from 'domains/order/Order.types';
import { getClaimFormType, isExadisOrderItem } from 'domains/order/Order.utils';
import { isRefNumberDeposit } from 'domains/references/DepositReference';
import { OrderDetailProps } from 'pages/MyOrdersPage/AscOrders/AscOrderDetail';
import { getAscOrderDeliveryStatus } from 'pages/MyOrdersPage/AscOrders/Tables/Statuses';
import {
  getDeliveryOrPackageDisplay,
  getPrefillReason,
  getReferenceDisplay,
  getSupplierNameWithOrderNumberDisplay,
} from 'pages/MyOrdersPage/AscOrders/utils';
import AddToDepositReturnListButton from 'pages/MyOrdersPage/DepositReturnList/AddToDepositReturnListButton';
import AddToExadisReturnListButton from 'pages/MyOrdersPage/ExadisReturnList/AddToExadisReturnListButton';
import { CenterFlex, Flex, PlusCircleButton, Text, WithTooltip } from 'UI';
import { textFormatter } from 'utils';
import { isValidDate } from 'utils/format/formattingService';

interface AftersalesOrderItemForRender extends AftersalesOrderItem {
  mainLine: boolean;
  totalDeliveries: number;
  rowKey: string;
}

const buildAddExadisReturnListRequest = (
  orderDetailData: AftersalesOrder,
  orderItem: AftersalesOrderItem,
  invoice?: AftersalesInvoice,
): AddExadisReturnListItemSelection => ({
  salesOrderNumber: orderDetailData.salesOrderNumber ?? '',
  invoiceId: invoice?.invoiceNumber ?? '',
  invoiceLineNumber: invoice?.invoiceLineNumber ?? 0,
  reference: orderItem.reference ?? '',
  requestedQuantity: 0,
  sourceInvoicedQuantity: Number(invoice?.invoiceQuantity) || 0,
});

const buildAddDepositReturnListRequest = (
  orderDetailData: AftersalesOrder,
  orderItem: AftersalesOrderItem,
  invoice: AftersalesInvoice,
): AddDepositReturnListItemSelection => ({
  salesOrderNumber: orderDetailData.salesOrderNumber ?? '',
  invoiceId: invoice.invoiceNumber ?? '',
  invoiceLineNumber: invoice.invoiceLineNumber ?? 0,
  reference: orderItem.reference ?? '',
  requestedQuantity: 0,
  sourceInvoicedQuantity: Number(invoice.invoiceQuantity) || 0,
});

const getMultipleLinesOrderDetails = (orderRow: AftersalesOrderItem[]): AftersalesOrderItemForRender[] => {
  const transformedData: AftersalesOrderItemForRender[] = [];
  orderRow.forEach((item) => {
    const purchaseOrder = item.purchaseOrder;
    const purchaseOrderItem = purchaseOrder?.purchaseOrderItem;
    const deliveries = purchaseOrderItem?.deliveries;
    if (deliveries && deliveries.length > 0 && purchaseOrder && purchaseOrderItem) {
      deliveries.forEach((delivery, index) => {
        transformedData.push({
          ...item,
          mainLine: index === 0,
          totalDeliveries: deliveries.length ?? 1,
          rowKey: `${item.itemNumber}-${index}`,
          purchaseOrder: {
            ...purchaseOrder,
            purchaseOrderItem: {
              ...purchaseOrderItem,
              deliveries: [delivery],
            },
          },
        });
      });
    } else {
      transformedData.push({
        ...item,
        mainLine: true,
        totalDeliveries: 1,
        rowKey: `${item.itemNumber}-0`,
      });
    }
  });
  return transformedData;
};

const AscOrderDetailTable = ({ orderDetail, setIsOpenCreateClaim }: OrderDetailProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const claimForm = useSelector(getOrderClaimForm);
  const exadisReturnListEnabled = useSelector(getExadisReturnListEnabled);
  const depositReturnListEnabled = useSelector(getDepositReturnListEnabled);

  const transformedTableData = useMemo(() => getMultipleLinesOrderDetails(orderDetail.items ?? []), [orderDetail]);

  const createClaimFromDetail = (orderItem: AftersalesOrderItem, invoice?: AftersalesInvoice) => {
    const invoiceNumber = invoice?.invoiceNumber ?? '';
    const invoiceLineNumber = invoice?.invoiceLineNumber;
    const invoiceQuantity = invoice?.invoiceQuantity;

    const getQuantityInvoiced = () => {
      const qty = invoiceQuantity ? Number.parseFloat(invoiceQuantity) : 0;
      return qty > 0 ? qty : undefined;
    };

    const reason = getPrefillReason(orderItem.reference, claimForm.reason);

    dispatch(
      setOrderClaimForm({
        ...initialState.claims.claimForm,
        reason,
        formType: getClaimFormType(orderItem.salesOrderLinePlant, isExadisOrderItem(orderItem)),
        orderNumber: orderDetail.salesOrderNumber,
        invoiceNumber,
        invoiceLineNumber,
        referenceNumberOrdered: orderItem.reference,
        soldToPartyId: orderDetail.soldToPartyId,
        shipToPartyId: orderDetail.shipToPartyId,
        quantityInvoiced: getQuantityInvoiced(),
      }),
    );
    setIsOpenCreateClaim?.(true);
  };

  const handleClaimClick = (row: AftersalesOrderItemForRender, invoice?: AftersalesInvoice) => () =>
    createClaimFromDetail(row, invoice);

  const getColumns = () => [
    {
      title: t('asc_order.order_detail.order_line', 'Order line'),
      dataIndex: 'itemNumber',
      key: 'itemNumber',
      sorter: (a: AftersalesOrderItemForRender, b: AftersalesOrderItemForRender) =>
        Number(a.itemNumber ?? 0) - Number(b.itemNumber ?? 0),
      defaultSortOrder: 'ascend' as const,
      render: (itemNumber: string) => <CenterFlex>{itemNumber ?? ''}</CenterFlex>,
    },
    {
      title: t('asc_order.order_detail.supplier', 'Supplier'),
      dataIndex: ['purchaseOrder', 'supplierName'],
      key: 'supplierName',
      render: (_supplierName?: string, row?: AftersalesOrderItemForRender) => (
        <Flex justify={'center'} align={'center'}>
          <WithTooltip
            title={getSupplierNameWithOrderNumberDisplay(
              row?.purchaseOrder?.supplierName,
              row?.purchaseOrder?.purchaseOrderNumber,
            )}
          >
            <Text type="base">
              {getSupplierNameWithOrderNumberDisplay(
                row?.purchaseOrder?.supplierName,
                row?.purchaseOrder?.purchaseOrderNumber,
              )}
            </Text>
          </WithTooltip>
        </Flex>
      ),
    },
    {
      title: t('asc_order.order_detail.reference', 'Reference'),
      dataIndex: 'reference',
      key: 'reference',
      render: (reference: string) => <CenterFlex>{getReferenceDisplay(reference)}</CenterFlex>,
    },
    {
      title: t('asc_order.order_detail.requested_qty', 'Requested qty'),
      dataIndex: ['purchaseOrder', 'purchaseOrderItem', 'purchaseOrderItemQuantity'],
      key: 'purchaseOrderItemQuantity',
      render: (purchaseOrderItemQuantity: string) => <CenterFlex>{purchaseOrderItemQuantity ?? ''}</CenterFlex>,
    },
    {
      title: t('asc_order.order_detail.delivery_qty', 'Delivery qty'),
      dataIndex: ['purchaseOrder', 'purchaseOrderItem', 'deliveries', 0, 'deliveryQuantity'],
      key: 'deliveryQuantity',
      render: (deliveryQuantity?: string) => <CenterFlex>{deliveryQuantity ?? ''}</CenterFlex>,
    },
    {
      title: t('asc_order.order_detail.delivery_status', 'Delivery status'),
      dataIndex: ['purchaseOrder', 'purchaseOrderItem', 'deliveries', 0, 'deliveryStatus'],
      key: 'deliveryStatus',
      render: (deliveryStatus?: string) => (
        <CenterFlex>{getAscOrderDeliveryStatus(deliveryStatus ?? '', t)}</CenterFlex>
      ),
    },
    {
      title: t('asc_order.order_detail.delivery_number', 'Delivery number'),
      dataIndex: ['purchaseOrder', 'purchaseOrderItem', 'deliveries', 0],
      key: 'deliveryNumber',
      render: (_delivery: unknown, row: AftersalesOrderItemForRender) => (
        <CenterFlex>
          {getDeliveryOrPackageDisplay(row.salesOrderLinePlant, row.purchaseOrder?.purchaseOrderItem?.deliveries?.[0])}
        </CenterFlex>
      ),
    },
    {
      title: t('asc_order.order_detail.delivery_date', 'Delivery date'),
      dataIndex: ['purchaseOrder', 'purchaseOrderItem', 'deliveries', 0, 'deliveryDate'],
      key: 'deliveryDate',
      render: (deliveryDate?: string) => {
        if (!deliveryDate || !isValidDate(deliveryDate)) {
          return null;
        }
        return <CenterFlex>{textFormatter.formatDateNumeric(new Date(deliveryDate))}</CenterFlex>;
      },
    },
    {
      title: t('asc_order.order_detail.invoice_number', 'Invoice number'),
      dataIndex: ['purchaseOrder', 'purchaseOrderItem', 'deliveries', 0, 'invoice', 'invoiceNumber'],
      key: 'invoiceNumber',
      render: (invoiceNumber?: string) => <CenterFlex>{invoiceNumber ?? ''}</CenterFlex>,
    },
    {
      title: t('order.invoice_line_number', 'Invoice Line number'),
      dataIndex: ['purchaseOrder', 'purchaseOrderItem', 'deliveries', 0, 'invoice', 'invoiceLineNumber'],
      key: 'invoiceLineNumber',
      sorter: (a: AftersalesOrderItemForRender, b: AftersalesOrderItemForRender) =>
        Number(a.purchaseOrder?.purchaseOrderItem?.deliveries?.[0]?.invoice?.invoiceLineNumber ?? 0) -
        Number(b.purchaseOrder?.purchaseOrderItem?.deliveries?.[0]?.invoice?.invoiceLineNumber ?? 0),
      render: (invoiceLineNumber?: number) => <CenterFlex>{invoiceLineNumber ?? ''}</CenterFlex>,
    },
    {
      title: t('asc_order.order_detail.invoice_date', 'Invoice date'),
      dataIndex: ['purchaseOrder', 'purchaseOrderItem', 'deliveries', 0, 'invoice', 'invoiceCreationDate'],
      key: 'invoiceCreationDate',
      render: (invoiceCreationDate?: string) => {
        if (!invoiceCreationDate || !isValidDate(invoiceCreationDate)) {
          return null;
        }
        return <CenterFlex>{textFormatter.formatDateNumeric(new Date(invoiceCreationDate))}</CenterFlex>;
      },
    },
    {
      title: t('asc_order.order_detail.invoice_net_amount', 'Invoice net amount'),
      dataIndex: ['purchaseOrder', 'purchaseOrderItem', 'deliveries', 0, 'invoice', 'invoiceNetAmount'],
      key: 'invoiceNetAmount',
      render: (invoiceNetAmount?: string) => (
        <CenterFlex>{textFormatter.formatCurrency(invoiceNetAmount ?? 0, orderDetail.currency)}</CenterFlex>
      ),
    },
    {
      title: t('asc_order.order_detail.invoice_qty', 'Invoice qty'),
      dataIndex: ['purchaseOrder', 'purchaseOrderItem', 'deliveries', 0, 'invoice', 'invoiceQuantity'],
      key: 'invoiceQuantity',
      render: (invoiceQuantity?: string) => <CenterFlex>{invoiceQuantity ?? ''}</CenterFlex>,
    },
    {
      title: t('order.claim.claim', 'Claim'),
      dataIndex: ['purchaseOrder', 'purchaseOrderItem', 'deliveries', 0, 'invoice'],
      key: 'invoice',
      render: (invoice: AftersalesInvoice, row: AftersalesOrderItemForRender) => (
        <CenterFlex>
          {isExadisOrderItem(row) &&
            (isRefNumberDeposit(row.reference) && depositReturnListEnabled ? (
              <AddToDepositReturnListButton
                item={buildAddDepositReturnListRequest(orderDetail, row, invoice)}
                data-cy={`add-to-deposit-return-list-${row.itemNumber}`}
              />
            ) : (
              exadisReturnListEnabled &&
              isExadisOrderItem(row) && (
                <AddToExadisReturnListButton
                  item={buildAddExadisReturnListRequest(orderDetail, row, invoice)}
                  data-cy={`add-to-exadis-return-list-${row.itemNumber}`}
                />
              )
            ))}
          <PlusCircleButton
            color={'brand_black'}
            onClick={handleClaimClick(row, invoice)}
            disabled={!invoice?.invoiceNumber}
            data-cy={`create-claim-${row.itemNumber}`}
          />
        </CenterFlex>
      ),
      fixed: 'right' as const,
    },
  ];

  return (
    <Table
      bordered
      dataSource={transformedTableData}
      pagination={false}
      columns={getColumns()}
      rowKey={'rowKey'}
      scroll={{ x: true }}
    />
  );
};

export default AscOrderDetailTable;
